import { z } from 'zod';

/**
 * The contract for every list a shop can outgrow.
 *
 * Every list endpoint was `select … limit(500)` with no search, no offset and no total,
 * and the table filtered and paged that array in the browser. At 800 customers the shop
 * saw 500 of them, the footer said "500 rows" as though that were all of them, and typing
 * a name into the search box searched only the half that had been downloaded. The 301st
 * customer was not hard to find — they were unreachable.
 *
 * ── Offset, not a cursor ──
 *
 * Deliberate. The table shows "page 3 / 12" and a total, and a cursor can express neither.
 * Cursors win on feeds of millions where rows shift under you; a shop's customer list is
 * thousands at the outside and changes a few times a day. Offset also survives sorting by
 * any column, which a cursor only does with the sort key baked into it. If a shop ever
 * grows past what offset can serve, that's a real signal to revisit — not a reason to
 * carry the complexity now.
 *
 * ── Which lists take this ──
 *
 * The ones bounded only by how big the business gets: customers, jobs, quotes, invoices,
 * requests, projects. NOT the ones bounded by a parent — the contacts on one customer, the
 * visits on one job, the lines on one invoice. Those are small by construction, and paging
 * three contacts would be ceremony with no purpose.
 */

/** Big enough that most shops never page; small enough to stay honest on a phone. */
export const LIST_LIMIT_DEFAULT = 50;
export const LIST_LIMIT_MAX = 200;

export const listInput = z.object({
  /** Matched against a few columns per entity, server-side. Blank means "everything". */
  search: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(LIST_LIMIT_MAX).default(LIST_LIMIT_DEFAULT),
  offset: z.coerce.number().int().min(0).default(0),
  /**
   * A column key the entity's service knows how to sort by. An unknown key falls back to
   * the entity's default order rather than erroring — a stale bookmark from before a
   * column was renamed should still show the list.
   */
  sort: z.string().trim().max(40).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  /**
   * Per-column filters, keyed by the table's column keys. Each service resolves keys
   * through its own allow-list map (the orderFor pattern) — an unknown key is IGNORED,
   * never an error and never SQL. Matching is token-prefix, mirroring the client
   * table's `columnFilterMatches`: "IL" finds "Ottawa IL 61350", "612" does not find
   * "STP612GR". Bounded because it is user-controlled: a table has a handful of
   * filterable columns and nobody types a 120-char filter.
   */
  filters: z.record(z.string().min(1).max(40), z.string().trim().max(120)).optional(),
});
export type ListInput = z.infer<typeof listInput>;

/**
 * `total` is the count of everything matching the search — not the length of `rows`.
 * That difference is the entire point: it's what lets a footer say "50 of 812" truthfully
 * instead of counting the array it happens to be holding.
 */
export interface ListResult<T> {
  rows: T[];
  total: number;
}
